import { QueryClient, QueryFunction } from '@tanstack/react-query';
import * as Linking from 'expo-linking';
import { isNative, isWeb } from '@/lib/platform';
import { config } from '../config/env';
import { betterAuthClient } from './betterAuthClient';
import { clientLogger } from './datadog';

// Avoid 304 responses breaking fetch json parsing in local web (no body). Force no-store outside production on web.
const cacheMode: RequestCache =
  isWeb || process.env.NODE_ENV !== 'production' ? 'no-store' : 'default';

export class ApiTimeoutError extends Error {
  readonly code = 'ETIMEDOUT';
  readonly url: string;
  readonly method: string;
  readonly timeoutMs: number;

  constructor(url: string, method: string, timeoutMs: number) {
    super(`Request timed out after ${timeoutMs}ms (${method} ${url})`);
    this.name = 'ApiTimeoutError';
    this.url = url;
    this.method = method;
    this.timeoutMs = timeoutMs;
  }
}

export function buildAuthHeaders(): HeadersInit {
  const headers: Record<string, string> = {};
  if (!isWeb) {
    const cookies = betterAuthClient.getCookie?.();
    if (cookies) {
      headers.cookie = cookies;
    }
    headers['expo-origin'] = Linking.createURL('', { scheme: 'pckm' });
    headers['x-skip-oauth-proxy'] = 'true';
  }
  return headers;
}

async function fetchWithTimeout(
  url: string,
  options: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  if (!timeoutMs || timeoutMs <= 0) {
    return fetch(url, options);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const existingSignal = options.signal;
  if (existingSignal) {
    if (existingSignal.aborted) {
      controller.abort();
    } else {
      existingSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }
  }

  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } catch (error) {
    if (controller.signal.aborted) {
      throw new ApiTimeoutError(url, String(options.method ?? 'GET'), timeoutMs);
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Helper function to throw if response is not ok
 */
async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

/**
 * Default query function that constructs URLs from query keys
 * Similar to the web version - joins query key array elements to form the URL
 */
type UnauthorizedBehavior = 'returnNull' | 'throw';
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Construct URL from query key array
    // Query keys like ['/api/leagues', id] become '/api/leagues/:id'
    const path = queryKey.join('/');
    const url = path.startsWith('http') ? path : `${config.apiUrl}${path}`;

    if (__DEV__) {
      clientLogger.debug('Default QueryFn: GET', { url });
    }

    const headers = buildAuthHeaders();
    const credentialsMode: RequestCredentials = isWeb ? 'include' : 'omit';

    let res: Response;
    try {
      res = await fetchWithTimeout(
        url,
        {
          credentials: credentialsMode,
          headers,
          cache: cacheMode,
        },
        config.apiTimeoutMs,
      );
    } catch (error) {
      if (error instanceof ApiTimeoutError) {
        clientLogger.warn('[queryClient] Query timed out', {
          url,
          timeoutMs: error.timeoutMs,
          queryKey: queryKey.join('/'),
        });
      }
      throw error;
    }

    if (unauthorizedBehavior === 'returnNull' && res.status === 401) {
      clientLogger.warn('[queryClient] Query returned 401', {
        url,
        queryKey: queryKey.join('/'),
        mode: unauthorizedBehavior,
      });
      return null;
    }

    await throwIfResNotOk(res);

    if (__DEV__) {
      clientLogger.debug('Default QueryFn: Success', {
        status: res.status,
        url,
      });
    }

    return await res.json();
  };

const isNativePlatform = isNative;

// Create and export query client with default configuration
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: 'throw' }),
      retry: false, // Match web behavior - don't retry failed requests
      refetchOnMount: true,
      refetchOnWindowFocus: !isNativePlatform,
      refetchOnReconnect: true,
      staleTime: isNativePlatform ? Infinity : 0,
      gcTime: isNativePlatform ? Infinity : 1000 * 60 * 5,
    },
    mutations: {
      retry: false,
    },
  },
});

/**
 * Make an authenticated API request to the backend
 *
 * @param url - The API endpoint (e.g., '/api/leagues' or 'https://full-url.com/api/leagues')
 * @param method - HTTP method (GET, POST, PUT, DELETE)
 * @param data - Request body data (will be JSON stringified)
 * @returns Promise<Response> - The fetch Response object
 */
type ApiRequestOptions = {
  timeoutMs?: number;
};

export async function apiRequest(
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'GET',
  data?: any,
  options?: ApiRequestOptions,
): Promise<Response> {
  // Construct full URL if relative path provided
  const fullUrl = url.startsWith('http') ? url : `${config.apiUrl}${url}`;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...buildAuthHeaders(),
  };
  const credentialsMode: RequestCredentials = isWeb ? 'include' : 'omit';

  const requestOptions: RequestInit = {
    method,
    headers,
    credentials: credentialsMode,
    cache: cacheMode,
  };

  // Add body for methods that support it
  if (data && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    requestOptions.body = JSON.stringify(data);
  }

  if (__DEV__) {
    clientLogger.debug('API Request', { method, url: fullUrl, hasBody: Boolean(data) });
  }

  try {
    const response = await fetchWithTimeout(
      fullUrl,
      requestOptions,
      options?.timeoutMs ?? config.apiTimeoutMs,
    );

    if (__DEV__) {
      clientLogger.debug('API Response', { method, url: fullUrl, status: response.status });
    }
    if (response.status === 401) {
      clientLogger.warn('[queryClient] API request returned 401', {
        method,
        url: fullUrl,
      });
    }

    return response;
  } catch (error) {
    if (error instanceof ApiTimeoutError) {
      clientLogger.warn('[queryClient] API request timed out', {
        method,
        url: fullUrl,
        timeoutMs: error.timeoutMs,
      });
    }
    if (__DEV__) {
      clientLogger.error('API Request Failed', {
        method,
        url: fullUrl,
        error: error instanceof Error ? error.message : String(error),
      });
    }
    throw error;
  }
}

/**
 * Helper function to make an API request and automatically parse JSON response
 *
 * @param url - The API endpoint
 * @param method - HTTP method
 * @param data - Request body data
 * @returns Promise<T> - Parsed JSON response
 * @throws Error if response is not ok
 */
export async function apiRequestJson<T = any>(
  url: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'GET',
  data?: any,
  options?: ApiRequestOptions,
): Promise<T> {
  const response = await apiRequest(url, method, data, options);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.message || `API request failed with status ${response.status}`;
    throw new Error(errorMessage);
  }

  return response.json();
}
